import React, { useState, useEffect } from 'react';
import { db, auth } from "../FirebaseConfig";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function AddPropertyPage(props) {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [ListingProfile, setListingProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    price: "",
    bedrooms: 1,
    bathrooms: 1,
    type: "apartment",
    amenities: "",
    });

  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState([]);

  // Get Auth User ID Safely
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        navigate("/Login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch Profile Info
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const docRef = doc(db, "users", userId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          const { name, phoneNumber, gender, role, profilePic } = data;
          const isProfileIncomplete = !name || !phoneNumber || !gender || !role || !profilePic;

          if (isProfileIncomplete) {
            alert("Please complete your profile to list a property.");
            navigate("/ListForm");
            return;
          }

          setListingProfile(data);
        } else {
          alert("Please complete your profile before listing a property.");
          navigate("/ListForm");
        }
      } catch (err) {
        console.error("Failed to fetch user profile", err);
        alert("Error loading your profile. Try again.");
        navigate("/ListForm");
      } finally {
        setLoadingProfile(false);
      }
    };

    if (userId) fetchProfile();
  }, [userId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    setUploading(true);
    const files = e.target.files;
    const urls = [];

    for (let file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "unsigned_ibuyunn");

      try {
        const res = await axios.post(`https://api.cloudinary.com/v1_1/dm8uirujp/upload`, formData);
        if (res.data.secure_url) {
          urls.push(res.data.secure_url);
        }
      } catch (err) {
        alert("One or more images failed to upload. Try again.");
        console.error("Upload error:", err);
        setUploading(false);
        return;
      }
    }

    setUploadedUrls(urls);
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ListingProfile) {
      alert("Please complete your profile before listing a property.");
      navigate("/ListForm");
      return;
    }

    if (formData.description.trim().split(" ").length < 25) {
      alert("Description should be at least 25 words long.");
      return;
    }

    if (uploadedUrls.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    try {
      await addDoc(collection(db, "listings"), {
        ...formData,
        amenities: formData.amenities.split(",").map((a) => a.trim()),
        images: uploadedUrls,
        postedBy: userId,
        createdAt: serverTimestamp(),
         status: "available", // 👈 Default status
        expiryTime: null      // 👈 Will be set when marked taken
      });

      alert("Property listed successfully!");
      setFormData({
        title: "",
        description: "",
        location: "",
        price: "",
        bedrooms: 1,
        bathrooms: 1,
        type: "apartment",
        amenities: "",
        coordinates: null,
      });
      setUploadedUrls([]);
    } catch (err) {
      console.error("Error saving listing:", err);
      alert("Something went wrong. Please try again.");
    }
  };

  if (loadingProfile) {
    return <p className="text-white text-center mt-4">Loading profile...</p>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-transparent border-gold border-2 mt-4 shadow rounded">
      <h1 className="text-2xl font-bold text-gold mb-4">List a Property</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          name="title"
          placeholder="Title"
          className="w-full border p-2 bg-transparent border-gold text-white"
          onChange={handleChange}
          value={formData.title}
          required
        />
        <textarea
          name="description"
          placeholder="Description (min. 25 words)"
          className="w-full border p-2 bg-transparent border-gold text-white"
          onChange={handleChange}
          value={formData.description}
          required
        />
        <input
          type="text"
          name="location"
          placeholder="Location"
          className="w-full border p-2 bg-transparent border-gold text-white"
          onChange={handleChange}
          value={formData.location}
          required
        />
        <input
          type="number"
          name="price"
          placeholder="Price per Year (₦)"
          className="w-full border p-2 bg-transparent border-gold text-white"
          onChange={handleChange}
          value={formData.price}
          required
        />

        <div className="flex gap-4">
          <div className="w-full">
            <label className="text-white text-sm mb-1 block">Bedrooms</label>
            <input
              type="number"
              name="bedrooms"
              placeholder="Bedrooms"
              className="w-full border p-2 bg-transparent border-gold text-white"
              onChange={handleChange}
              value={formData.bedrooms}
              required
            />
          </div>
          <div className="w-full">
            <label className="text-white text-sm mb-1 block">Bathrooms</label>
            <input
              type="number"
              name="bathrooms"
              placeholder="Bathrooms"
              className="w-full border p-2 bg-transparent border-gold text-white"
              onChange={handleChange}
              value={formData.bathrooms}
              required
            />
          </div>
        </div>

        <select
          name="type"
          className="w-full border p-2 bg-transparent border-gold text-white"
          onChange={handleChange}
          value={formData.type}
        >
          <option value="apartment">Apartment</option>
          <option value="room">Room</option>
          <option value="flat">Flat</option>
          <option value="house">House</option>
        </select>

        <input
          type="text"
          name="amenities"
          placeholder="Amenities (comma separated)"
          className="w-full border p-2 bg-transparent border-gold text-white"
          onChange={handleChange}
          value={formData.amenities}
        />

        <p className="text-white font-semibold">Upload max of 4 images</p>
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageUpload}
        />
        {uploading && <p className="text-blue-500">Uploading images...</p>}

        <button
          type="submit"
          className="bg-green-900 text-white w-full px-4 py-2 rounded hover:bg-green-800"
          disabled={uploading}
        >
          Submit
        </button>
      </form>
    </div>
  );
}
